import React, { useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon
} from '@mui/icons-material';
// ⚡ ÍCONE PARA GERIR PAPÉIS
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PropTypes from 'prop-types';
import { formatarData } from '../../utils/formatters';
import { atualizarPerfil } from '../../services/perfilService';
import useNotificacoes from '../../hooks/useNotificacoes';
import EditarPerfilForm from '../../components/Instituicoes/EditarPerfilForm';

const GRUPOS_PERMISSOES = {
  'Gestão': [
    'gerenciar_campanhas',
    'gerenciar_preinscricoes',
    'gerenciar_planos_pagamento',
    'acessar_relatorios'
  ],
  'Financeiro': [
    'gerenciar_planos_pagamento',
    'acessar_relatorios_financeiros',
    'vincular_estudantes_planos'
  ],
  'Acadêmico': [
    'gerenciar_cursos',
    'gerenciar_classes',
    'gerenciar_periodos',
    'gerenciar_disciplinas',
    'gerenciar_preinscricoes'
  ],
  'Conteúdo': [
    'gerenciar_conteudo',
    'gerenciar_sliders',
    'gerenciar_destaques'
  ]
};

const ListaPerfis = ({ perfis, onExcluir, onReloadPerfis, instituicaoId, onGerirPapeis }) => {
  const [perfilParaExcluir, setPerfilParaExcluir] = useState(null);
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);
  const [perfilSelecionado, setPerfilSelecionado] = useState(null);
  const [perfilParaEditar, setPerfilParaEditar] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notificacoes = useNotificacoes();

  const handleAbrirConfirmacao = useCallback((perfil) => {
    setPerfilParaExcluir(perfil);
  }, []);

  const handleFecharConfirmacao = useCallback(() => {
    setPerfilParaExcluir(null);
  }, []);

  const handleConfirmarExclusao = useCallback(() => {
    if (perfilParaExcluir) {
      onExcluir(perfilParaExcluir.id);
      handleFecharConfirmacao();
    }
  }, [perfilParaExcluir, onExcluir, handleFecharConfirmacao]);

  const handleAbrirDetalhes = useCallback((perfil) => {
    setPerfilSelecionado(perfil);
    setDetalhesAbertos(true);
  }, []);

  const handleFecharDetalhes = useCallback(() => {
    setDetalhesAbertos(false);
    setPerfilSelecionado(null);
  }, []);

  const handleAbrirEdicao = useCallback((perfil) => {
    setPerfilParaEditar({
      ...perfil,
      permissoesOriginais: [...perfil.permissoes]
    });
  }, []);

  const handleSalvarEdicao = useCallback(async (dadosAtualizados) => {
    try {
      const dadosParaEnviar = {
        id: dadosAtualizados.id,
        tipo: dadosAtualizados.tipo,
        status: dadosAtualizados.status,
        permissoes: dadosAtualizados.permissoes,
        instituicaoId: instituicaoId
      };

      await atualizarPerfil(dadosAtualizados.id, dadosParaEnviar);
      setPerfilParaEditar(null);

      if (notificacoes?.adicionarNotificacao) {
        notificacoes.adicionarNotificacao('Perfil atualizado com sucesso', 'success');
      } else {
        setSnackbar({
          open: true,
          message: 'Perfil atualizado com sucesso',
          severity: 'success'
        });
      }

      if (onReloadPerfis) {
        onReloadPerfis();
      }
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      if (notificacoes?.adicionarNotificacao) {
        notificacoes.adicionarNotificacao(
          error.message || 'Erro ao atualizar perfil',
          'error'
        );
      } else {
        setSnackbar({
          open: true,
          message: error.message || 'Erro ao atualizar perfil',
          severity: 'error'
        });
      }
    }
  }, [instituicaoId, onReloadPerfis, notificacoes]);

  const handleFecharSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const getStatusChipColor = useCallback((status) => {
    switch (status) {
      case 'ATIVO':
        return 'success';
      case 'INATIVO':
        return 'error';
      default:
        return 'default';
    }
  }, []);

  const getNomeGrupoPermissao = useCallback((permissao) => {
    for (const [grupo, permissoes] of Object.entries(GRUPOS_PERMISSOES)) {
      if (permissoes.includes(permissao)) {
        return grupo;
      }
    }
    return 'Outras';
  }, []);

  const getPermissoesAgrupadas = useCallback((permissoes) => {
    return permissoes.reduce((grupos, permissao) => {
      const grupo = getNomeGrupoPermissao(permissao);
      if (!grupos[grupo]) {
        grupos[grupo] = [];
      }
      grupos[grupo].push(permissao);
      return grupos;
    }, {});
  }, [getNomeGrupoPermissao]);

  if (!perfis.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Nenhum perfil cadastrado.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {perfis.map((perfil) => (
              <TableRow key={perfil.id}>
                <TableCell>{perfil.Usuario?.nome || '-'}</TableCell>
                <TableCell>{perfil.Usuario?.username || '-'}</TableCell>
                <TableCell>{perfil.Usuario?.email || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={perfil.tipo}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={perfil.status}
                    size="small"
                    color={getStatusChipColor(perfil.status)}
                  />
                </TableCell>
                <TableCell>
                  {formatarData(perfil.createdAt)}
                </TableCell>
                <TableCell align="center">
                  {/* ⚡ NOVO: botão Gerir Papéis */}
                  {onGerirPapeis && (
                    <Tooltip title="Gerir Papéis do Usuário">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => onGerirPapeis(perfil)}
                      >
                        <ManageAccountsIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="Ver detalhes">
                    <IconButton
                      size="small"
                      onClick={() => handleAbrirDetalhes(perfil)}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleAbrirEdicao(perfil)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleAbrirConfirmacao(perfil)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog
        open={!!perfilParaExcluir}
        onClose={handleFecharConfirmacao}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o perfil de {perfilParaExcluir?.Usuario?.nome || '-'}?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharConfirmacao}>Cancelar</Button>
          <Button onClick={handleConfirmarExclusao} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Detalhes do Perfil */}
      <Dialog
        open={detalhesAbertos}
        onClose={handleFecharDetalhes}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalhes do Perfil</DialogTitle>
        <DialogContent>
          {perfilSelecionado && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Nome:</strong> {perfilSelecionado.Usuario?.nome || '-'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Username:</strong> {perfilSelecionado.Usuario?.username || '-'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Email:</strong> {perfilSelecionado.Usuario?.email || '-'}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Tipo:</strong> {perfilSelecionado.tipo}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong> {perfilSelecionado.status}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Criado em:</strong> {formatarData(perfilSelecionado.createdAt)}
              </Typography>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                <strong>Permissões:</strong>
              </Typography>

              {Object.entries(getPermissoesAgrupadas(perfilSelecionado.permissoes)).map(([grupo, permissoes]) => (
                <Box key={grupo} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {grupo}:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {permissoes.map((permissao) => (
                      <Chip
                        key={permissao}
                        label={permissao.replace(/_/g, ' ').toLowerCase()}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharDetalhes}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleFecharSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleFecharSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de Edição do Perfil */}
      <EditarPerfilForm
        perfil={perfilParaEditar}
        open={Boolean(perfilParaEditar)}
        onClose={() => setPerfilParaEditar(null)}
        onSave={handleSalvarEdicao}
      />
    </>
  );
};

ListaPerfis.propTypes = {
  perfis: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      Usuario: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        nome: PropTypes.string,
        username: PropTypes.string,
        email: PropTypes.string
      }),
      tipo: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      permissoes: PropTypes.arrayOf(PropTypes.string).isRequired
    })
  ).isRequired,
  onExcluir: PropTypes.func.isRequired,
  onReloadPerfis: PropTypes.func.isRequired,
  instituicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // ⚡ callback opcional para abrir o modal de papéis
  onGerirPapeis: PropTypes.func
};

export default ListaPerfis;
